import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { Select, TextInput } from '../../components/ui';
import Badge from '../../components/ui/Badge';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import { ReportTextCell, CrewDataTableFrame, CrewDataTablePrompt, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import type { AppraisalNrResponse, AppraisalNrRow } from './types';

const AppraisalNrPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const [nrOnly, setNrOnly] = useState(false);
  const [query, setQuery] = useState('');

  const { data, error, isLoading, mutate } = useSWR<AppraisalNrResponse>(
    '/crew/reports/appraisal-nr',
    fetcher,
    { revalidateOnFocus: true },
  );

  const rows = useMemo(() => {
    const list = data?.rows ?? [];
    const q = query.trim().toLowerCase();
    return list.filter((row) => {
      if (nrOnly && !row.notRecommended) return false;
      if (!q) return true;
      const hay = [row.name, row.rank, row.nationality, row.overallEvaluation, row.vessel]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [data?.rows, nrOnly, query]);

  const columns: Column<AppraisalNrRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Crew',
        sortable: true,
        render: (_v, row) => (
          <Link to={`/crew/${row.crewId}`} state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline', colors.primaryFg)} onClick={(e) => e.stopPropagation()}>
            {row.name}
          </Link>
        )},
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'nationality', header: 'Nationality', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'notRecommended',
        header: 'NR',
        sortable: true,
        render: (v) =>
          v ? (
            <Badge variant="danger" className="rounded-md px-2 py-0.5">
              NR
            </Badge>
          ) : (
            <ReportTextCell value="" />
          )},
      {
        key: 'lastAppraisalDate',
        header: 'Last appraisal',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      { key: 'overallEvaluation', header: 'Overall evaluation', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'appraisalType', header: 'Type', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'vessel', header: 'Vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'toBePromoted',
        header: 'Promote',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? 'Yes' : null} />},
    ],
    [],
  );

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{
          title: 'Appraisals / NR history',
          body: 'Crew with appraisal history and/or not-recommended flag. Shows last appraisal date, overall evaluation, and promotion mark.'}}
        onSearch={() => mutate()}
        loading={isLoading}
        context={
          data ? `${data.summary.total} crew · ${data.summary.notRecommended} not recommended` : undefined
        }
      >
        <CrewReportFilterField label="Filter" width="sm">
          <Select
            name="nrOnly"
            value={nrOnly ? 'nr' : 'all'}
            onChange={(e) => setNrOnly(e.target.value === 'nr')}
            options={[
              { value: 'all', label: 'All' },
              { value: 'nr', label: 'NR only' },
            ]}
            showClearButton={false}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Search" width="fill">
          <TextInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Crew, rank, evaluation…" />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {error ? (
          <CrewDataTablePrompt><span className="text-red-600">Could not load appraisals.</span></CrewDataTablePrompt>
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            keyExtractor={(row) => row.crewId}
            loading={isLoading}
            emptyMessage="No appraisal / NR records"
            onRowClick={(row) => openCrew(row.crewId)}
            actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
            actionsPosition="left"
          />
        )}
      </CrewDataTableFrame>
    </div>
  );
};

export default AppraisalNrPage;
